import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useGetAvgCardValuePerDayQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod, AvgCardValueDataPoint } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';
import { formatBucketTick, formatBucketLabel } from '../utils/formatBucket';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const AVG_COLOR = '#8409D3';

interface TooltipPayload {
  payload: AvgCardValueDataPoint;
}

const AvgTooltip: React.FC<{ active?: boolean; payload?: TooltipPayload[]; label?: string; period: AnalyticsPeriod }> = ({
  active,
  payload,
  label,
  period
}) => {
  if (!active || !payload || !payload.length || !label) return null;
  const row = payload[0].payload;
  if (row.avgValue == null) return null;
  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '8px',
        color: '#fff',
        padding: '8px 12px',
        fontSize: 13
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{formatBucketLabel(label, period)}</div>
      <div>{row.avgValue.toFixed(1)} points</div>
      <div style={{ color: '#999' }}>across {row.cardCount.toLocaleString()} cards</div>
    </div>
  );
};

const AvgCardValueChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error, refetch } = useGetAvgCardValuePerDayQuery({ period });

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Average Card Value</h2>
          <p className="chart-subtitle">Avg points value per card in completed trades (UTC)</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend">
            <span className="legend-dot avg-card-value" />
            <span className="legend-label">Avg Value</span>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">
            Failed to load data
            <button className="chart-retry-btn" onClick={() => refetch()}>Retry</button>
          </div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatBucketTick}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                interval={data.data.length > 60 ? Math.floor(data.data.length / 12) : 0}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                label={{
                  value: 'Avg Card Value (points)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#999', fontSize: 12, textAnchor: 'middle' }
                }}
              />
              <Tooltip
                content={<AvgTooltip period={period} />}
                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              />
              <Bar
                dataKey="avgValue"
                fill={AVG_COLOR}
                radius={[4, 4, 0, 0]}
                name="Avg Card Value"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : null}
      </div>
    </div>
  );
};

export default AvgCardValueChart;
